import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import es from 'date-fns/locale/es';
import api from '../../utils/api';
import Cookies from 'js-cookie'; // Para manejar el token CSRF
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../styles/calendario.css';


// Configurar el localizer para date-fns
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { 'es': es },
});

function Calendario() {
  const [eventos, setEventos] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(window.innerWidth <= 768 ? 'month' : 'month'); // Vista predeterminada según el tamaño de pantalla
  const [message, setMessage] = useState(''); // Estado para mensajes de éxito/error
  const [error, setError] = useState(''); // Estado para errores

  useEffect(() => {
    // Obtener las reservas desde el backend
    api.get(`reservas/`)
      .then(response => {
        const reservas = response.data.map(reserva => {
          const fecha = new Date(reserva.fecha);
          let start, end;

          // Determinar las horas de inicio y fin según la duración
          if (reserva.duracion === 'todo_dia') {
            start = new Date(fecha.setHours(8, 0));
            end = new Date(fecha.setHours(23, 59));
          } else if (reserva.duracion === 'manana') {
            start = new Date(fecha.setHours(8, 0));
            end = new Date(fecha.setHours(15, 0));
          } else if (reserva.duracion === 'tarde') {
            start = new Date(fecha.setHours(15, 0));
            end = new Date(fecha.setHours(23, 59));
          } else if (reserva.duracion === 'horario') {
            const [startHours, startMinutes] = reserva.hora_inicio.split(':').map(Number);
            const [endHours, endMinutes] = reserva.hora_fin.split(':').map(Number);
            start = new Date(fecha.setHours(startHours, startMinutes));
            end = new Date(fecha.setHours(endHours, endMinutes));
          }

          return {
            id: reserva.id, // Añadimos el ID para usarlo al eliminar
            title: `${reserva.sala.nombre} - ${reserva.actividad} (${reserva.usuario})`,
            start,
            end,
            sala: reserva.sala.nombre,
            actividad: reserva.actividad,
            usuario: reserva.usuario,
            duracion: reserva.duracion,
            duracion_display: reserva.duracion_display,
          };
        });
        setEventos(reservas);
      })
      .catch(error => {
        console.error('Error fetching reservas:', error);
        setError('Error al cargar las reservas');
      });

    // Ajustar la vista al redimensionar la ventana
    const handleResize = () => {
      setView(window.innerWidth <= 768 ? 'month' : 'month');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Manejador para eliminar una reserva
  const handleDeleteEvent = async (eventId) => {
    const csrftoken = Cookies.get('csrftoken'); // Obtener el token CSRF
    try {
      await api.delete(`reservas/${eventId}/`, {
        withCredentials: true,
        headers: {
          'X-CSRFToken': csrftoken, // Incluir el token CSRF en la solicitud
        },
      });
      // Actualizar el estado para eliminar el evento del calendario
      setEventos(eventos.filter(evento => evento.id !== eventId));
      setSelectedEvent(null); // Cerrar el cuadro de detalles
      setMessage('Reserva eliminada con éxito');
      setError('');
    } catch (error) {
      console.error('Error deleting reserva:', error);
      // Mostrar el mensaje de error del backend si está disponible
      const errorMessage = error.response?.data?.error || 'Error al eliminar la reserva';
      setError(errorMessage);
      setMessage('');
    }
  };

  // Manejador para cuando se selecciona un evento
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setMessage(''); // Limpiar mensajes al seleccionar un evento
    setError('');
  };

  // Manejador para cerrar el cuadro de detalles
  const handleCloseDetails = () => {
    setSelectedEvent(null);
    setMessage('');
    setError('');
  };

  // Manejador para los cambios de navegación (anterior, siguiente, hoy)
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  // Manejador para los cambios de vista (mes, semana, día, agenda)
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Establecer la hora mínima a las 8:00 AM
  const minTime = new Date();
  minTime.setHours(8, 0, 0);

  return (
    <div className="page">
      <h1>Calendario de Reservas</h1>
      {message && <p className="success">{message}</p>}
      {error && <p className="error">{error}</p>}
      <div className="calendario-wrapper">
        {/* Calendario */}
        <div className="calendario-container">
          <Calendar
            localizer={localizer}
            events={eventos}
            startAccessor="start"
            endAccessor="end"
            style={{ margin: '20px' }}
            onSelectEvent={handleSelectEvent}
            date={currentDate}
            onNavigate={handleNavigate}
            view={view}
            onView={handleViewChange}
            min={minTime}
            messages={{
              allDay: 'Todo el día',
              previous: 'Anterior',
              next: 'Siguiente',
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día',
              agenda: 'Agenda',
              date: 'Fecha',
              time: 'Hora',
              event: 'Evento',
              noEventsInRange: 'No hay eventos en este rango.',
            }}
          />
        </div>

        {/* Cuadro de detalles */}
        {selectedEvent && (
          <div className="details-container">
            <button
              onClick={handleCloseDetails}
              className="details-close-button"
            >
              ✕
            </button>
            <h3>Detalles de la Reserva</h3>
            <p><strong>Sala:</strong> {selectedEvent.sala}</p>
            <p><strong>Actividad:</strong> {selectedEvent.actividad}</p>
            <p><strong>Usuario:</strong> {selectedEvent.usuario}</p>
            <p><strong>Duración:</strong> {selectedEvent.duracion_display}</p>
            <p>
              <strong>Inicio:</strong>{' '}
              {selectedEvent.start.toLocaleString('es-ES', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
            <p>
              <strong>Fin:</strong>{' '}
              {selectedEvent.end.toLocaleString('es-ES', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
            <button
              onClick={() => handleDeleteEvent(selectedEvent.id)}
              className="delete-button"
            >
              Eliminar Reserva
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Calendario;